import { Collection, Product, VidAppJWT, Video } from "@/api";
import { createContext, ReactNode, useCallback, useMemo } from "react";
import { useAuthToken } from "@/providers/context";
import { jwtDecode } from "jwt-decode";
import { VIDEO_UNLOCKED } from "@/const.ts";
import { differenceInDays } from "date-fns";
import { useAppProperties } from "@/hooks";

interface ContextValue extends Partial<VidAppJWT> {
  isCollectionUnlocked: (
    collection?: Pick<Collection, "Properties">,
  ) => boolean;
  isVideoUnlocked: (
    video?: Pick<Video, "SourceProductIDs" | "ProductId">,
  ) => boolean;
  isCollectionDripLocked: (collection: Collection) => boolean;
  userIsSubscribed: boolean;
  platform: Product["platform"] | "";
  isAdminUser: (id: string) => boolean;
  loggedInUserIsAdmin: boolean;
}

interface ProviderProps {
  children: ReactNode | ReactNode[];
}

type OptionalContextValue = ContextValue | undefined;
const UserContext = createContext<OptionalContextValue>(undefined);

const UserProvider = ({ children }: ProviderProps) => {
  const { token } = useAuthToken();
  const decoded = useMemo(() => {
    if (token) {
      return jwtDecode<VidAppJWT>(token);
    }
    return {} as Partial<VidAppJWT>;
  }, [token]);
  const { data: properties } = useAppProperties();

  const AdminUserIDs = useMemo(() => {
    const propertyValue = properties?.AdminUserIDs;
    if (!propertyValue) {
      return [];
    }

    try {
      const parsed = JSON.parse(propertyValue);
      return parsed as string[];
    } catch {
      return [];
    }
  }, [properties?.AdminUserIDs]);

  const isAdminUser = useCallback(
    (id: string) => AdminUserIDs.includes(id),
    [AdminUserIDs],
  );

  const loggedInUserIsAdmin = useMemo(
    () => isAdminUser(decoded.uid ?? ""),
    [decoded.uid, isAdminUser],
  );

  const platform = useMemo(() => {
    const { detailed_products } = decoded;
    if (!detailed_products || detailed_products.length === 0) {
      return ""; // No subscription or loading
    }
    return detailed_products[0].platform;
  }, [decoded]);

  const products = decoded.products;

  const isVideoUnlocked = useCallback(
    (input?: Pick<Video, "SourceProductIDs" | "ProductId">) => {
      if (loggedInUserIsAdmin) {
        return true;
      }
      if (!input) {
        return false;
      }
      const { SourceProductIDs, ProductId } = input;

      if (ProductId === VIDEO_UNLOCKED) {
        return true; // Free Video
      }

      if (SourceProductIDs) {
        const ownedProducts = products || [];
        for (const _id of SourceProductIDs) {
          if (ownedProducts.includes(_id)) {
            return true;
          }
        }
      }

      return false;
    },
    [products, loggedInUserIsAdmin],
  );
  const isCollectionUnlocked = useCallback(
    (input?: Pick<Collection, "Properties">) => {
      if (loggedInUserIsAdmin) {
        return true;
      }
      if (!input) {
        return false;
      }
      if (token) {
        return true;
      }
      const { LockedUntilSignIn } = input.Properties ?? {};
      return LockedUntilSignIn !== "1";
    },
    [token, loggedInUserIsAdmin],
  );

  const isCollectionDripLocked = useCallback(
    (collection: Collection) => {
      if (loggedInUserIsAdmin) {
        return false;
      }

      const dripEnabled = collection.Drip === 1;
      const dripDays = collection.DripDays;
      const { SourceProductIDs } = collection;

      if (!dripEnabled || !dripDays || dripDays === 0) {
        return false;
      }

      const { detailed_products } = decoded;

      if (!token || !detailed_products) {
        return false;
      }

      const purchaseDates: Date[] = [];

      if (SourceProductIDs) {
        for (const product of detailed_products) {
          if (
            SourceProductIDs.includes(product.source_product_id) &&
            product.purchase_date
          ) {
            purchaseDates.push(new Date(product.purchase_date));
          }
        }
      }

      if (purchaseDates.length === 0) {
        return true;
      }

      const earliestPurchaseDate = purchaseDates.sort(
        (a, b) => a.getTime() - b.getTime(),
      )[0];

      const elapsedDays = differenceInDays(new Date(), earliestPurchaseDate);

      return elapsedDays < dripDays;
    },
    [token, decoded, loggedInUserIsAdmin],
  );
  const userIsSubscribed = useMemo(() => {
    if (loggedInUserIsAdmin) {
      return true;
    }
    if (products) {
      return products.length > 0;
    }
    return false;
  }, [products, loggedInUserIsAdmin]);

  return (
    <UserContext.Provider
      value={{
        ...decoded,
        platform,
        isCollectionUnlocked,
        isCollectionDripLocked,
        isVideoUnlocked,
        userIsSubscribed,
        isAdminUser,
        loggedInUserIsAdmin,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export { UserProvider, UserContext };
